import React, { useState, useEffect, useRef, useCallback } from 'react';
import { uploadImages, fetchUploadAssets } from '../../lib/hostApi';
import FramedImage from '../FramedImage';

export default function UploadManager({
  images = [],
  onImagesChange,
  token,
  multiple = true,
  label = 'Product Gallery Images',
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [showAssetLibrary, setShowAssetLibrary] = useState(false);
  const [libraryAssets, setLibraryAssets] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const loadLibraryAssets = useCallback(async () => {
    try {
      const assets = await fetchUploadAssets(token, searchQuery);
      setLibraryAssets(assets || []);
    } catch (err) {
      console.error('Failed to load asset library:', err);
    }
  }, [searchQuery, token]);

  useEffect(() => {
    if (showAssetLibrary) {
      loadLibraryAssets();
    }
  }, [showAssetLibrary, loadLibraryAssets]);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    setUploadProgress(20);
    setError(null);

    try {
      setUploadProgress(60);
      const newAssets = await uploadImages(files, token, setUploadProgress);
      setUploadProgress(100);

      const newUrls = newAssets.map((asset) => asset.url);
      if (multiple) {
        onImagesChange([...images, ...newUrls]);
      } else {
        onImagesChange([newUrls[0]]);
      }
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleRemoveImage = (indexToRemove) => {
    const updated = images.filter((_, idx) => idx !== indexToRemove);
    onImagesChange(updated);
  };

  const handleMoveImage = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= images.length) return;
    const updated = [...images];
    const temp = updated[index];
    updated[index] = updated[targetIndex];
    updated[targetIndex] = temp;
    onImagesChange(updated);
  };

  const handleSelectFromLibrary = (url) => {
    if (multiple) {
      if (!images.includes(url)) {
        onImagesChange([...images, url]);
      }
    } else {
      onImagesChange([url]);
    }
  };

  return (
    <div className="upload-manager-container my-3">
      <label className="form-label font-weight-bold d-flex justify-content-between align-items-center">
        <span>{label}</span>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => setShowAssetLibrary(!showAssetLibrary)}
        >
          {showAssetLibrary ? '❌ Close Library' : '📁 Choose from Upload Library'}
        </button>
      </label>

      {/* Drag and Drop Zone */}
      <div
        className={`upload-dropzone p-4 text-center border rounded ${
          dragOver ? 'border-primary bg-light-purple' : 'border-dashed'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        style={{
          cursor: 'pointer',
          borderStyle: 'dashed',
          borderWidth: '2px',
          borderRadius: '12px',
          transition: 'all 0.2s ease',
        }}
      >
        <input
          type="file"
          ref={fileInputRef}
          className="d-none"
          accept="image/*"
          multiple={multiple}
          onChange={(e) => handleFiles(e.target.files)}
        />

        <div className="upload-icon mb-2" style={{ fontSize: '2.5rem' }}>
          📤
        </div>
        <p className="mb-1 font-weight-bold">
          Click to upload or drag & drop images here
        </p>
        <span className="text-muted small">
          Supports PNG, JPG, WEBP, and GIF up to 5 MB each
        </span>

        {uploading && (
          <div className="progress mt-3" style={{ height: '8px' }}>
            <div
              className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
              role="progressbar"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
        )}
      </div>

      {error && <div className="alert alert-danger mt-2 py-1 px-3 small">{error}</div>}

      {/* Current Images Gallery / Ordering */}
      {images.length > 0 && (
        <div className="current-images-gallery mt-3">
          <span className="small text-muted mb-2 d-block">
            Gallery Items ({images.length}) - Use arrows to reorder:
          </span>
          <div className="d-flex flex-wrap gap-3">
            {images.map((url, idx) => (
              <div
                key={idx}
                className="image-card position-relative border rounded p-1 shadow-sm overflow-hidden"
                style={{ width: '110px', backgroundColor: 'var(--card-background-color)' }}
              >
                <div style={{ width: '100%', height: '85px' }} className="rounded mb-1 overflow-hidden">
                  <FramedImage
                    src={url}
                    alt={`Product ${idx + 1}`}
                  />
                </div>
                <div className="d-flex justify-content-between px-1">
                  <button
                    type="button"
                    className="btn btn-xs btn-light py-0 px-1"
                    disabled={idx === 0}
                    onClick={() => handleMoveImage(idx, -1)}
                    title="Move Left"
                  >
                    ◀
                  </button>
                  <button
                    type="button"
                    className="btn btn-xs btn-light py-0 px-1"
                    disabled={idx === images.length - 1}
                    onClick={() => handleMoveImage(idx, 1)}
                    title="Move Right"
                  >
                    ▶
                  </button>
                  <button
                    type="button"
                    className="btn btn-xs btn-outline-danger py-0 px-1"
                    onClick={() => handleRemoveImage(idx)}
                    title="Remove"
                  >
                    🗑️
                  </button>
                </div>
                {idx === 0 && (
                  <span className="badge badge-custom badge-bestseller position-absolute top-0 start-0 m-1" style={{ zIndex: 3 }}>
                    Cover
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Asset Library Modal / Tray */}
      {showAssetLibrary && (
        <div className="asset-library-tray mt-3 border rounded p-3 bg-card shadow-sm">
          <div className="d-flex justify-content-between mb-2">
            <h6 className="m-0 font-weight-bold">Previously Uploaded Assets</h6>
            <input
              type="text"
              placeholder="Search assets..."
              className="form-control form-control-sm w-50"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div
            className="d-flex flex-wrap gap-2 overflow-auto"
            style={{ maxHeight: '200px' }}
          >
            {libraryAssets.length === 0 ? (
              <span className="text-muted small p-2">No assets found in library.</span>
            ) : (
              libraryAssets.map((asset) => (
                <div
                  key={asset.id}
                  className="asset-item border rounded p-1 position-relative overflow-hidden"
                  style={{
                    cursor: 'pointer',
                    width: '80px',
                    height: '75px',
                    borderColor: images.includes(asset.url) ? 'var(--primary-color)' : 'var(--border-color)',
                  }}
                  onClick={() => handleSelectFromLibrary(asset.url)}
                >
                  <FramedImage
                    src={asset.url}
                    alt={asset.filename || 'Asset'}
                  />
                  {images.includes(asset.url) && (
                    <span className="badge bg-success position-absolute top-0 end-0 m-1" style={{ zIndex: 3 }}>
                      ✓
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
